import { useState, useEffect, useCallback } from 'react';
import { Percent, Save } from 'lucide-react';
import { useGeneralMultipliers, type GeneralMultiplier } from '@/hooks/useMultipliers';
import { useSetProjectMultipliers } from '@/hooks/useProjects';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';

interface GeneralMultipliersPanelProps {
  projectId: string;
  enabledMultiplierIds: string[];
}

export function GeneralMultipliersPanel({
  projectId,
  enabledMultiplierIds,
}: GeneralMultipliersPanelProps) {
  const { data, isLoading } = useGeneralMultipliers();
  const setMultipliers = useSetProjectMultipliers();

  // Local state for enabled multipliers
  const [enabledIds, setEnabledIds] = useState<Set<string>>(new Set(enabledMultiplierIds));
  const [hasChanges, setHasChanges] = useState(false);

  // Reset when props change
  useEffect(() => {
    setEnabledIds(new Set(enabledMultiplierIds));
    setHasChanges(false);
  }, [enabledMultiplierIds]);

  const handleToggle = useCallback((multiplierId: string) => {
    setEnabledIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(multiplierId)) {
        newSet.delete(multiplierId);
      } else {
        newSet.add(multiplierId);
      }
      return newSet;
    });
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    const multipliers = (data?.multipliers || []).map((m) => ({
      multiplierId: m.id,
      isEnabled: enabledIds.has(m.id),
    }));

    await setMultipliers.mutateAsync({ projectId, multipliers });
    setHasChanges(false);
  }, [data, enabledIds, projectId, setMultipliers]);

  // Parse multiplier factor for display
  const parseMultiplierFactor = (multiplierStr: string): number => {
    // Pattern: "+0.3x"
    const xMatch = multiplierStr.match(/\+?(\d+\.?\d*)x/i);
    if (xMatch) {
      return 1 + parseFloat(xMatch[1]);
    }
    // Pattern: "+30%"
    const pctMatch = multiplierStr.match(/\+?(\d+\.?\d*)%/i);
    if (pctMatch) {
      return 1 + parseFloat(pctMatch[1]) / 100;
    }
    return 1;
  };

  // Calculate total factor from enabled multipliers
  const totalFactor = (data?.multipliers || [])
    .filter((m) => enabledIds.has(m.id))
    .reduce((acc, m) => acc + (parseMultiplierFactor(m.multiplier) - 1), 1);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  const multipliers = data?.multipliers || [];

  if (multipliers.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
        No general multipliers configured. Contact an admin to add multipliers.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-medium text-gray-900">
          <Percent className="h-4 w-4" />
          General Multipliers
        </h4>
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={setMultipliers.isPending}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            Save Changes
          </button>
        )}
      </div>

      <p className="text-xs text-gray-500">
        Enable multipliers that apply to this project. These affect all activity pricing calculations.
      </p>

      {/* Multiplier List */}
      <div className="space-y-2">
        {multipliers.map((multiplier) => (
          <MultiplierToggle
            key={multiplier.id}
            multiplier={multiplier}
            isEnabled={enabledIds.has(multiplier.id)}
            onToggle={() => handleToggle(multiplier.id)}
            factor={parseMultiplierFactor(multiplier.multiplier)}
          />
        ))}
      </div>

      {/* Total Factor */}
      {enabledIds.size > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-amber-800">Total Multiplier Effect</span>
            <span className="text-lg font-bold text-amber-900">{totalFactor.toFixed(2)}x</span>
          </div>
          <p className="mt-1 text-xs text-amber-700">
            All activity days will be multiplied by this factor (before overhead).
          </p>
        </div>
      )}
    </div>
  );
}

// Individual multiplier toggle component
interface MultiplierToggleProps {
  multiplier: GeneralMultiplier;
  isEnabled: boolean;
  onToggle: () => void;
  factor: number;
}

function MultiplierToggle({ multiplier, isEnabled, onToggle, factor }: MultiplierToggleProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border p-3 transition-colors',
        isEnabled
          ? 'border-blue-300 bg-blue-50'
          : 'border-gray-200 bg-white hover:bg-gray-50'
      )}
    >
      <label className="relative inline-flex cursor-pointer items-center">
        <input
          type="checkbox"
          checked={isEnabled}
          onChange={onToggle}
          className="peer sr-only"
        />
        <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300" />
      </label>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{multiplier.name}</span>
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono text-gray-600">
            {multiplier.code}
          </span>
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-xs font-medium',
              isEnabled ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            )}
          >
            {factor.toFixed(2)}x
          </span>
        </div>
        {multiplier.description && (
          <p className="mt-0.5 text-xs text-gray-500">{multiplier.description}</p>
        )}
        <p className="mt-1 text-xs text-gray-400">{multiplier.multiplier}</p>
      </div>
    </div>
  );
}
